import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Icon
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import CategoryIcon from '@mui/icons-material/Category';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import AppService from '../../../../Configurations/Appservice';
import { useDispatch } from 'react-redux';
import {
  showLoader,
  dismissLoader,
  showSuccessMessage,
} from '../../../../LayoutContainers/AppLayout/redux/AppSlice';
import CategoryFormDialog from './CategoryFormDialog';

/**
 * Categories Management Page
 * Lists all document categories with CRUD operations
 * Uses mock data during UI-First phase
 */
const CategoriesPage = () => {
  const dispatch = useDispatch();
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [selectedVertical, setSelectedVertical] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Load data from API
  useEffect(() => {
    loadVerticals();
    loadCategories();
  }, []);

  const loadVerticals = () => {
    AppService.get('api/verticals/list-details')
      .then((response) => {
        const activeVerticals = (response || []).filter(v => v.isActive);
        setVerticals(activeVerticals);
        // Default to first active vertical
        if (activeVerticals.length > 0 && !selectedVertical) {
          setSelectedVertical(activeVerticals[0].id);
        }
      })
      .catch((error) => {
        console.error('Failed to load verticals', error);
      });
  };

  const loadCategories = () => {
    setLoading(true);
    AppService.get('api/organization-document-categories/list-details')
      .then((response) => {
        setCategories(response || []);
      })
      .catch((error) => {
        console.error('Failed to load categories', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Filter categories based on search term and selected vertical
  useEffect(() => {
    let filtered = categories;

    if (selectedVertical) {
      filtered = filtered.filter(c => c.verticalId === selectedVertical || c.verticalId?.toString() === selectedVertical?.toString());
    }

    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.code.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort by display order
    filtered = [...filtered].sort((a, b) => a.displayOrder - b.displayOrder);

    setFilteredCategories(filtered);
  }, [searchTerm, selectedVertical, categories]);

  const handleAddClick = () => {
    setSelectedCategory(null);
    setDialogOpen(true);
  };

  const handleEditClick = (category) => {
    setSelectedCategory(category);
    setDialogOpen(true);
  };

  const handleDeleteClick = (category) => {
    if (category.documentTypeCount > 0) {
      alert('Cannot delete category with document types. Remove document types first.');
      return;
    }
    setSelectedCategory(category);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirmClose = () => {
    setDeleteConfirmOpen(false);
    setSelectedCategory(null);
  };

  const handleDelete = () => {
    dispatch(showLoader());
    AppService.delete(`api/organization-document-categories/${selectedCategory.id}`)
      .then(() => {
        dispatch(showSuccessMessage('Category deleted successfully'));
        loadCategories();
        setDeleteConfirmOpen(false);
        setSelectedCategory(null);
      })
      .catch((error) => {
        console.error('Failed to delete category', error);
      })
      .finally(() => {
        dispatch(dismissLoader());
      });
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedCategory(null);
  };

  const handleSave = async (categoryData) => {
    dispatch(showLoader());
    const requestData = {
      ...categoryData,
      verticalId: selectedVertical,
    };
    if (selectedCategory) {
      // Update existing
      AppService.put(`api/organization-document-categories/${selectedCategory.id}`, requestData)
        .then(() => {
          dispatch(showSuccessMessage('Category updated successfully'));
          loadCategories();
          handleDialogClose();
        })
        .catch((error) => {
          console.error('Failed to update category', error);
        })
        .finally(() => {
          dispatch(dismissLoader());
        });
    } else {
      // Add new
      AppService.post('api/organization-document-categories', requestData)
        .then(() => {
          dispatch(showSuccessMessage('Category created successfully'));
          loadCategories();
          handleDialogClose();
        })
        .catch((error) => {
          console.error('Failed to create category', error);
        })
        .finally(() => {
          dispatch(dismissLoader());
        });
    }
  };

  const getVerticalName = (verticalId) => {
    const vertical = verticals.find(v => v.id === verticalId);
    return vertical?.name || 'Unknown';
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CategoryIcon color="primary" />
            Document Categories
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure document categories for each vertical (License, Government Cards, Tax, etc.)
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          disabled={!selectedVertical}
        >
          Add Category
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Vertical</InputLabel>
          <Select
            value={selectedVertical}
            onChange={(e) => setSelectedVertical(e.target.value)}
            label="Vertical"
          >
            {verticals.map((vertical) => (
              <MenuItem key={vertical.id} value={vertical.id}>
                {vertical.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Search categories..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            )
          }}
          sx={{ width: 300 }}
        />
      </Box>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={40}></TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Icon</TableCell>
              <TableCell align="center">Color</TableCell>
              <TableCell align="center">Doc Types</TableCell>
              <TableCell align="center">Order</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  Loading...
                </TableCell>
              </TableRow>
            ) : filteredCategories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  {selectedVertical ? 'No categories found' : 'Select a vertical to view categories'}
                </TableCell>
              </TableRow>
            ) : (
              filteredCategories.map((category) => (
                <TableRow key={category.id} hover>
                  <TableCell>
                    <DragIndicatorIcon color="disabled" fontSize="small" sx={{ cursor: 'grab' }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {category.code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">{category.name}</Typography>
                      {category.nameAr && (
                        <Typography variant="caption" color="text.secondary" dir="rtl">
                          {category.nameAr}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Icon fontSize="small" color="action">{category.icon}</Icon>
                      <Typography variant="caption" color="text.secondary">
                        {category.icon}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: 1,
                        backgroundColor: category.colour || category.color,
                        mx: 'auto'
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={category.documentTypeCount}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{category.displayOrder}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={category.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={category.isActive ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEditClick(category)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(category)}
                        disabled={category.documentTypeCount > 0}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Total: {filteredCategories.length} categories
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Active: {filteredCategories.filter(c => c.isActive).length}
        </Typography>
      </Box>

      {/* Form Dialog */}
      <CategoryFormDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleSave}
        category={selectedCategory}
        verticalId={selectedVertical}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteConfirmClose}>
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the category "{selectedCategory?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteConfirmClose}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoriesPage;
